// A portfolio is a set of accounts; an account is cash plus holdings; a holding
// is a unit count against a symbol in the instrument pool (data/instruments.ts).
// Loans hang off the portfolio, since a HELOC is borrowed against the household
// rather than against any one account.

export type AccountKind = 'tfsa' | 'rrsp' | 'margin' | 'cash';

export const ACCOUNT_KIND_LABEL: Record<AccountKind, string> = {
    tfsa: 'TFSA',
    rrsp: 'RRSP',
    margin: 'Margin',
    cash: 'Cash',
};

/**
 * The accounts every portfolio has, and the only definition of that anywhere.
 *
 * There is no add-an-account screen and there does not need to be: a Canadian
 * retail investor holds at most one of each of these, so the whole set is always
 * on screen and an unused one reads as empty.
 *
 * These are not seeded. No row exists for an account until something is written
 * into it — see materialise() in portfolio-store. The database had this list
 * copied into it three times over (a signup trigger, a backfill, create_plan)
 * and all three had drifted apart; there is now one copy, here, and Postgres
 * learns the label and the flag when it is told to make the row.
 *
 * `sheltered` is the field that matters downstream: it decides whether income is
 * taxed, and so whether interest on money borrowed into the account is
 * deductible.
 */
export const ACCOUNT_SHAPE: { kind: AccountKind; label: string; sheltered: boolean }[] = [
    { kind: 'tfsa', label: 'TFSA', sheltered: true },
    { kind: 'rrsp', label: 'RRSP', sheltered: true },
    { kind: 'margin', label: 'Margin', sheltered: false },
    { kind: 'cash', label: 'Cash', sheltered: false },
];

/** Derived, never stored: the sum of the buys for one symbol in one account.
    See holdingsByAccount in portfolio-store. */
export interface Holding {
    symbol: string;
    units: number;
    /** Total cost of the position including commissions. */
    bookCost: number;
}

export interface Account {
    id: string;
    kind: AccountKind;
    label: string;
    /** Registered accounts pay no tax on distributions; a margin account does. */
    sheltered: boolean;
    /** Total contributed to this account to date, in dollars. A cumulative
        record, not a recurring flow — it is what a contribution limit is
        measured against. */
    deposit: number;
}

/**
 * Lifetime contribution room by account kind, in dollars. A static figure for
 * now: the real number is per-person and depends on the years you have been
 * eligible, plus any withdrawals, which are not modelled.
 */
export const CONTRIBUTION_LIMITS: Partial<Record<AccountKind, number>> = {
    tfsa: 109_000,
};

/**
 * What kind of borrowing this is.
 *
 * A list rather than free text, and margin is the reason: a margin loan is
 * secured against the securities in the margin account itself, so it cannot
 * reach into a registered one — the broker has no claim on assets inside a TFSA
 * or an RRSP and will not lend against them. The app refuses that pairing, and
 * can only do so while "this is a margin loan" is a fact it can read.
 *
 * The other three carry no rule; they are there so the list can describe a real
 * facility without a shrug.
 */
export type LoanKind = 'heloc' | 'margin' | 'personal' | 'other';

export const LOAN_KIND_LABEL: Record<LoanKind, string> = {
    heloc: 'HELOC',
    margin: 'Margin loan',
    personal: 'Personal loan',
    other: 'Other',
};

export const LOAN_KINDS: LoanKind[] = ['heloc', 'margin', 'personal', 'other'];

/** Opening rate for a loan you have just added — a starting point in the box,
    not a fact, and the first thing anyone will correct. */
export const DEFAULT_LOAN_RATE = 5;

/**
 * One borrowing facility.
 *
 * Yours, not a portfolio's. You do not have a different HELOC when you are
 * planning: it is one facility at one rate with one balance owed, so the sandbox
 * and every plan are measured against the same row rather than against copies of
 * it that go out of date separately.
 */
export interface Loan {
    id: string;
    kind: LoanKind;
    /** What you call it. Distinct from the issuer: two facilities at one bank
        need telling apart, and one bank can be behind several. */
    label: string;
    /** Who lends it. A rate with no lender against it cannot be checked against
        a statement. Empty until set. */
    issuer: string;
    /** Drawn and owed today. Typed, not derived — the lender is the authority on
        it, and what the app derives is where that money went. */
    balance: number;
    /** What may be drawn in total, or null where the facility has no limit. A
        term loan is fully drawn by construction, and inventing a limit for it
        would report "0% used". */
    limit: number | null;
    /** Annual interest rate, %. */
    rate: number;
}

/** Headroom: what is left to draw. Null where there is no limit to measure
    against, negative never — a facility cannot be over-drawn without the lender
    having agreed to a larger one. */
export const loanAvailable = (l: Loan): number | null => (l.limit == null ? null : Math.max(0, l.limit - l.balance));

/** Which fixed workspace a portfolio is, or undefined for a saved plan. The two
    named slots are singular per user and are addressed by name, because the app
    renders them before there is a row to hold an id. */
export type PortfolioSlot = 'main' | 'sandbox';

export interface Portfolio {
    /** A uuid once stored. Until then a placeholder — see portfolio-store. */
    id: string;
    label: string;
    slot?: PortfolioSlot;
    accounts: Account[];
}

